import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { confirmImport, getImport, putImportPieces, retryImportPiece, type ImportDetail, type ImportPiece } from './api'
import { alreadyText, fileStatusLabel, nothingMade, problem, statusLabel } from './imports'
import { bodyAfter, hasBlankTitle, mergeWithNext, moveImage, rename, setDropped, setHero, splitAt, toBody } from './importSplit'
import { importsHref, pieceHref } from './links'
import { useEditorialTitle } from './titleContext'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const field = `min-h-9 rounded-lg border border-ink-400 bg-ink-800 px-2 font-ui text-[14px] text-paper ${focus}`

const Warnings = ({ items }: { items: string[] }) => items.length === 0 ? null : (
  <ul className="m-0 flex list-none flex-col gap-1 p-0 font-ui text-[13px] text-amber-glow">{items.map((w, i) => <li key={i}>{w}</li>)}</ul>
)

type Edit = (ps: ImportPiece[]) => ImportPiece[]
function PieceCard({ piece, pieces, onTitle, onEdit, disabled, busy }: {
  piece: ImportPiece; pieces: ImportPiece[]; onTitle: (title: string) => void; onEdit: (edit: Edit) => Promise<boolean>; disabled: boolean; busy: boolean
}) {
  const [splitting, setSplitting] = useState(false)
  const [heading, setHeading] = useState('')
  const [splitTitle, setSplitTitle] = useState('')
  const i = pieces.findIndex((x) => x.key === piece.key)
  const next = pieces[i + 1]
  const canMerge = !!next && next.file === piece.file
  const others = pieces.filter((x) => x.file === piece.file && x.key !== piece.key)
  const splitHeads = piece.headings.filter((h) => h.index > piece.start && h.index < piece.end)
  const pickHeading = (v: string) => {
    setHeading(v)
    setSplitTitle(piece.headings.find((h) => String(h.index) === v)?.text ?? '')
  }
  return (
    <li className={`flex flex-col gap-3 rounded-xl border border-ink-500 bg-ink-700 p-4 ${piece.dropped ? 'opacity-60' : ''}`}>
      <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">
        Title
        <input value={piece.title} disabled={busy} onChange={(e) => onTitle(e.target.value)} className={field} />
      </label>
      <div className="font-ui text-[13px] text-paper-dim">
        {piece.byline ? <>By {piece.byline} · </> : null}{piece.words} words
      </div>
      {piece.first_lines && <p className="m-0 line-clamp-3 font-serif text-[14px] italic text-paper-dim">{piece.first_lines}</p>}
      {piece.already_published && (
        <p role="status" className="m-0 font-ui text-[13px] text-amber-glow">
          {alreadyText(piece.already_published)}
        </p>
      )}
      <Warnings items={piece.warnings} />
      {piece.image_list.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
          {piece.image_list.map((im) => (
            <li key={im.index} className="flex w-36 flex-col gap-1 font-ui text-[12px] text-paper-dim">
              <img src={im.thumb} alt={im.caption ?? `Photo ${im.index}`} className="h-24 w-36 rounded object-cover" />
              <label className="flex items-center gap-1">
                <input type="radio" disabled={disabled} name={`hero-${piece.key}`} checked={piece.hero === im.index}
                  onChange={() => onEdit((ps) => setHero(ps, piece.key, im.index))} />
                Lead photo
              </label>
              {others.length > 0 && (
                <select aria-label={`Move photo ${im.index} to`} value="" disabled={disabled} className={field}
                  onChange={(e) => { const to = e.target.value; if (to) onEdit((ps) => moveImage(ps, piece.key, im.index, to)) }}>
                  <option value="">Move to…</option>
                  {others.map((o) => <option key={o.key} value={o.key}>{o.title || o.key}</option>)}
                </select>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn} disabled={!canMerge || disabled} onClick={() => onEdit((ps) => mergeWithNext(ps, piece.key))}>Merge with next</button>
        <button type="button" className={btn} disabled={splitHeads.length === 0 || disabled} onClick={() => setSplitting((s) => !s)}>Split at…</button>
        <button type="button" className={btn} disabled={disabled} onClick={() => onEdit((ps) => setDropped(ps, piece.key, !piece.dropped))}>{piece.dropped ? 'Keep' : 'Drop'}</button>
      </div>
      {splitting && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">
            Split at heading
            <select value={heading} onChange={(e) => pickHeading(e.target.value)} className={field}>
              <option value="">Choose a heading</option>
              {splitHeads.map((h) => <option key={h.index} value={h.index}>{h.text}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">
            Title of the new piece
            <input value={splitTitle} onChange={(e) => setSplitTitle(e.target.value)} className={field} />
          </label>
          <button type="button" className={btn} disabled={!heading || !splitTitle.trim() || disabled}
            onClick={async () => { if (await onEdit((ps) => splitAt(ps, piece.key, Number(heading), splitTitle.trim()))) { setSplitting(false); setHeading(''); setSplitTitle('') } }}>
            Split here
          </button>
        </div>
      )}
    </li>
  )
}

function Split({ data }: { data: ImportDetail }) {
  const qc = useQueryClient()
  const [edited, setEdited] = useState<ImportPiece[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pieces = edited ?? data.pieces
  const dirty = edited !== null
  const drafts = pieces.filter((p) => !p.dropped).length

  const blank = hasBlankTitle(pieces)

  // Local state only ever differs from the server by titles; any failed save leaves the server's structure on screen.
  const put = async (body: object[]): Promise<boolean> => {
    setBusy(true); setError(null)
    try {
      const out = await putImportPieces(data.id, body)
      qc.setQueryData(['editorial-import', data.id], out)
      setEdited(null)
      return true
    } catch (e) { setError(problem(e)); return false }
    finally { setBusy(false) }
  }
  const structural = (edit: Edit) => put(bodyAfter(pieces, edit))
  const confirmDrafts = async () => {
    if (dirty && !(await put(toBody(pieces)))) return
    setBusy(true)
    try { await confirmImport(data.id); await qc.invalidateQueries({ queryKey: ['editorial-import', data.id] }) }
    catch (e) { setError(problem(e)) }
    finally { setBusy(false) }
  }
  return (
    <>
      <h2 className="m-0 font-ui text-[20px] font-semibold text-paper">This upload looks like {pieces.length} {pieces.length === 1 ? 'piece' : 'pieces'}</h2>
      <Warnings items={data.warnings} />
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {pieces.map((p) => <PieceCard key={p.key} piece={p} pieces={pieces} disabled={busy || blank} busy={busy}
          onTitle={(t) => setEdited(rename(pieces, p.key, t))} onEdit={structural} />)}
      </ul>
      {error && <p role="alert" className="m-0 font-ui text-[14px] text-red-400">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" className={btn} disabled={!dirty || busy || blank} onClick={() => { void put(toBody(pieces)) }}>Save changes</button>
        <button type="button" className={btn} disabled={drafts === 0 || busy || blank} onClick={() => { void confirmDrafts() }}>Make {drafts} {drafts === 1 ? 'draft' : 'drafts'}</button>
      </div>
      {blank && <p className="m-0 font-ui text-[13px] text-paper-dim">Every piece needs a title.</p>}
    </>
  )
}

function Done({ data, path }: { data: ImportDetail; path: string }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const retry = async (key: string) => {
    setError(null)
    try { await retryImportPiece(data.id, key); await qc.invalidateQueries({ queryKey: ['editorial-import', data.id] }) }
    catch (e) { setError(problem(e)) }
  }
  return (
    <>
      {nothingMade(data.pieces) && <p role="status" className="m-0 font-ui text-[15px] text-red-400">Nothing was made.</p>}
      <Warnings items={data.warnings} />
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {data.pieces.map((p) => (
          <li key={p.key} className={`flex flex-col gap-1 rounded-lg border border-ink-500 bg-ink-700 p-3 font-ui text-[14px] text-paper ${p.dropped ? 'opacity-60' : ''}`}>
            <span className="flex flex-wrap items-center gap-3">
              <span className="min-w-0 flex-1">{p.title}</span>
              {p.dropped ? <span className="text-paper-dim">Left out</span>
                : p.draft_id != null ? <Link to={pieceHref(path, p.draft_id)} className="text-amber-glow underline underline-offset-2">Open draft</Link>
                : p.error ? <button type="button" className={btn} onClick={() => retry(p.key)}>Try again</button> : null}
            </span>
            {p.error && !p.dropped && <span role="alert" className="text-[13px] text-red-400">{p.error}</span>}
            <Warnings items={p.warnings} />
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="m-0 font-ui text-[14px] text-red-400">{error}</p>}
    </>
  )
}

export function ImportPage() {
  const { importId } = useParams()
  const id = Number(importId)
  const { section } = useEditorialTitle()
  const q = useQuery({
    queryKey: ['editorial-import', id], queryFn: () => getImport(id), enabled: Number.isFinite(id), retry: false,
    refetchInterval: (query) => { const s = query.state.data?.status; return s === 'converting' || s === 'creating' ? 3000 : false },
  })
  const data = q.data
  return (
    <div className="flex flex-col gap-5">
      <Link to={importsHref(section.path)} className="font-ui text-[13px] text-amber-glow underline underline-offset-2">All imports</Link>
      <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">Import</h1>
      {q.isLoading ? <p className="m-0 font-ui text-[14px] text-paper-dim">Loading…</p>
        : q.isError || !data ? <p role="alert" className="m-0 font-ui text-[14px] text-red-400">Could not load this import.</p> : (
          <>
            {!(data.status === 'done' && nothingMade(data.pieces)) && (
              <p role="status" className="m-0 font-ui text-[15px] text-paper">{statusLabel(data.status)}</p>
            )}
            {data.status === 'awaiting_split' ? <Split key={data.id} data={data} />
              : data.status === 'done' ? <Done data={data} path={section.path} />
              : (
                <>
                  {data.status === 'failed' && data.error && <p role="alert" className="m-0 font-ui text-[14px] text-red-400">{data.error}</p>}
                  <ul className="m-0 flex list-none flex-col gap-1 p-0 font-ui text-[14px] text-paper">
                    {data.files.map((f) => (
                      <li key={f.index}>{f.name}: {fileStatusLabel(f.status)}{f.error ? <span className="text-red-400"> — {f.error}</span> : null}</li>
                    ))}
                  </ul>
                  <Warnings items={data.warnings} />
                </>
              )}
          </>
        )}
    </div>
  )
}
